import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import StatSparkline from './StatSparkline';

/**
 * Enterprise KPI Card.
 *
 * Airy, data-forward layout: label → value → (optional) delta badge + sparkline.
 * Dark-mode compatible via CSS variables. Inherits direction from `<html dir>`
 * so no inline `dir={isRTL ? …}` is needed.
 */
export default function KPICard({
  icon: Icon,
  iconColor,
  iconBg,
  label,
  value,
  /** Legacy text trend */
  trend,
  trendLabel,
  /** New: numeric delta { value, direction } — preferred over `trend` */
  delta,
  /** New: numeric series for inline sparkline */
  sparkline,
  onClick,
  testId,
}) {
  const direction = delta?.direction ?? trend ?? null;
  const TrendIcon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;
  const trendColor =
    direction === 'up'
      ? 'var(--success-600, #10b981)'
      : direction === 'down'
        ? 'var(--danger-600, #ef4444)'
        : 'var(--text-tertiary, #94a3b8)';

  const clickable = typeof onClick === 'function';

  return (
    <div
      data-testid={testId}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className="kpi-card"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--radius-md, 12px)',
        padding: '1rem 1.125rem',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        border: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
      }}
      onMouseEnter={(e) => {
        if (!clickable) return;
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.08)';
        e.currentTarget.style.borderColor = 'var(--color-primary-dark, #355760)';
      }}
      onMouseLeave={(e) => {
        if (!clickable) return;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
        e.currentTarget.style.borderColor = 'var(--color-border)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        {Icon && (
          <span
            aria-hidden="true"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              backgroundColor: iconBg || 'rgba(53, 87, 96, 0.10)',
              color: iconColor || 'var(--color-primary-dark, #355760)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon size={16} />
          </span>
        )}
        <span
          style={{
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            lineHeight: 1.2,
          }}
        >
          {label}
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: '0.75rem',
        }}
      >
        <span
          style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            lineHeight: 1,
            letterSpacing: '-0.01em',
          }}
        >
          {value ?? '—'}
        </span>

        {Array.isArray(sparkline) && sparkline.length >= 2 && (
          <span
            style={{
              color: 'var(--color-primary-dark, #355760)',
              flexShrink: 0,
            }}
          >
            <StatSparkline values={sparkline} width={72} height={28} ariaLabel={label} />
          </span>
        )}
      </div>

      {(direction || trendLabel || delta?.label) && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: trendColor,
          }}
        >
          <TrendIcon size={14} aria-hidden="true" />
          <span>{delta?.label ?? trendLabel}</span>
        </div>
      )}
    </div>
  );
}
